import { Logic } from '~common/ui-logic/logic'
import type { TaskState } from '~/services/cache/types'
import type { AppContextType } from '~/app-context'
import { sleep } from '~common/utils/sleep'
import {
    getAllCategories,
    getAllCategories as getAllCategoriesFromService,
    getCategoriesFiltered,
    resolveCategoryLabel,
    SKRCategory,
    type TransactionCategoryType,
} from '~/features/transactions/services/categories'
import { getBookingCategoriesFromPlugin } from '~/features/plugins/utils'
import type { BookingCategories } from '~common/storage/users'
import { SortDirection } from '~common/components/sort-indicator'
import type { BankTransaction } from '~/storage/types'

export interface CategorySelectLogicDependencies {
    /** Callback when the category changes */
    onChange?: (categoryId: number | undefined) => void
    /** Callback when Enter is pressed in the input */
    onConfirm?: (categoryId: number | undefined) => void
    /** Placeholder text for the input */
    placeholder?: string
    /** Whether the component is disabled */
    disabled?: boolean
    /** Custom class name */
    className?: string
    /** Size variant */
    size?: 'small' | 'medium' | 'large'
    initValue?: number | undefined
    disableOnBlur?: boolean
    initActive?: boolean
    onSortClick?: () => void
    sortDirection?: SortDirection
    icon?: string
    width?: string
    variant?: 'tableHeader' | 'tableCell'
    clearOnClick?: (shiftKey?: boolean) => void
    resizable?: 'horizontal' | 'vertical' | 'both'
    /** For transaction row: transaction ID to refresh category matching */
    transactionId?: string
    /** For column header: filter mode */
    isFilter?: boolean
}

export interface CategorySelectLogicState {
    selectedCategoryId: number | undefined
    textValue: string
    isOpen: boolean
    active: boolean
    loadState: TaskState
    filterQuery: string
    highlightedIndex: number
    showTutorial: boolean
    expandedToAll: boolean
}

export class CategorySelectLogic extends Logic<
    CategorySelectLogicDependencies & AppContextType,
    CategorySelectLogicState
> {
    containerRef = { current: null as HTMLDivElement | null }

    getInitialState = (): CategorySelectLogicState => {
        const settings = this.deps.services.cache.getKey('settings')
        const bookingCategories = getBookingCategoriesFromPlugin(settings)
        const rawDefs = getAllCategoriesFromService(bookingCategories)
        const allDefs = Object.values(rawDefs).map((c) => ({
            ...c,
            id: parseInt(c.id, 10),
        }))
        const category = this.deps.initValue
            ? allDefs.find((c) => c.id === this.deps.initValue)
            : undefined
        return {
            selectedCategoryId: this.deps.initValue,
            textValue: category
                ? resolveCategoryLabel(category.id, bookingCategories)
                : '',
            isOpen: false,
            active: this.deps.initActive || true,
            loadState: 'pristine',
            filterQuery: '',
            highlightedIndex: 0,
            showTutorial: false,
            expandedToAll: false,
        }
    }

    async initialize() {
        const settings = await this.deps.services.cache.getSettings()
        const bookingCategories = getBookingCategoriesFromPlugin(settings)
        const rawDefs = getAllCategoriesFromService(bookingCategories)
        const allDefs = Object.values(rawDefs).map((c) => ({
            ...c,
            id: parseInt(c.id, 10),
        }))
        const category = this.deps.initValue
            ? allDefs.find((c) => c.id === this.deps.initValue)
            : undefined
        this.setState({
            selectedCategoryId: this.deps.initValue,
            textValue: category
                ? resolveCategoryLabel(category.id, bookingCategories)
                : '',
        })
    }

    shouldReload(
        oldDeps: CategorySelectLogicDependencies,
        newDeps: CategorySelectLogicDependencies,
    ): boolean {
        return oldDeps.initValue !== newDeps.initValue
    }

    formatCategoryDisplay(category: SKRCategory): string {
        return category.id + ' - ' + category.label
    }

    getAllActiveCategories(): Record<string, SKRCategory> {
        const settings = this.deps.services.cache.getKey('settings')
        const bookingCategories = getBookingCategoriesFromPlugin(settings)
        const activeCategories = bookingCategories?.activeCategories
        return getCategoriesFiltered({
            bookingCategories: {
                ...bookingCategories,
                activeCategories,
            },
        })
    }

    getFilteredCategories(): Record<string, SKRCategory> {
        const settings = this.deps.services.cache.getKey('settings')
        const bookingSettings = getBookingCategoriesFromPlugin(settings)
        let transactionType: TransactionCategoryType | undefined = undefined
        if (this.deps.transactionId) {
            const currentTransaction =
                this.deps.services.cache.getKey('transactions')?.[
                    this.deps.transactionId
                ]
            if (currentTransaction) {
                if (currentTransaction.amount < 0) {
                    transactionType = 'EXPENSE'
                } else {
                    transactionType = 'REVENUE'
                }
            }
        }

        const expenseCategories = getCategoriesFiltered({
            transactionType: transactionType,
            bookingCategories: bookingSettings,
            searchQuery: this.state.filterQuery || undefined,
        })

        return expenseCategories
    }

    handleInputChange = (newValue?: string): void => {
        const query = newValue || ''
        if (!query || query.trim() === '') {
            this.setState({
                selectedCategoryId: undefined,
                textValue: '',
                filterQuery: '',
                highlightedIndex: 0,
                expandedToAll: false,
                loadState: 'pristine',
            })
            this.deps.onChange?.(undefined)
            return
        }

        // Set input state
        this.setState({
            filterQuery: query,
            textValue: query,
            highlightedIndex: 0,
        })

        // Brief loading
        setTimeout(() => {
            this.setState({ loadState: 'success' })
        }, 150)
    }

    handleKeyDown = (key: string, currentValue: string) => {
        if (!this.state.isOpen) {
            if (key === 'Enter' || key === 'ArrowDown') {
                this.setIsOpen(true)
            }
            return
        }

        const filtered = Object.values(this.getFilteredCategories())

        if (key === 'ArrowDown') {
            const newIndex = Math.min(
                this.state.highlightedIndex + 1,
                filtered.length - 1,
            )
            this.setState({ highlightedIndex: newIndex })
        } else if (key === 'ArrowUp') {
            const newIndex = Math.max(this.state.highlightedIndex - 1, 0)
            this.setState({ highlightedIndex: newIndex })
        } else if (key === 'Enter') {
            if (filtered.length > 0) {
                const selectedCategory = filtered[this.state.highlightedIndex]
                if (selectedCategory) {
                    this.setState({
                        selectedCategoryId: Number(selectedCategory.id),
                        textValue: this.formatCategoryDisplay(selectedCategory),
                        isOpen: false,
                        filterQuery: '',
                        highlightedIndex: 0,
                        expandedToAll: false,
                        loadState: 'pristine',
                    })
                    this.deps.onChange?.(Number(selectedCategory.id))
                    this.deps.onConfirm?.(Number(selectedCategory.id))
                }
            } else {
                this.handleConfirm()
            }
        }
    }

    handleCategorySelect = (categoryId: number): void => {
        const settings = this.deps.services.cache.getKey('settings')
        this.setState({
            selectedCategoryId: categoryId,
            textValue: resolveCategoryLabel(
                categoryId,
                settings?.bookingCategories,
            ),
            isOpen: false,
            filterQuery: '',
            highlightedIndex: 0,
            expandedToAll: false,
            loadState: 'pristine',
        })
        this.deps.onChange?.(categoryId)
        this.deps.onConfirm?.(categoryId)
    }

    handleConfirm = () => {
        this.setState({
            isOpen: false,
        })
        this.deps.onConfirm?.(this.state.selectedCategoryId)
    }

    togglePopout = (currentIsOpen: boolean) => {
        if (!this.deps.disabled) {
            this.setState({ isOpen: !currentIsOpen })
        }
    }

    closePopout = () => {
        this.setState({
            isOpen: false,
            filterQuery: '',
            expandedToAll: false,
            loadState: 'pristine',
        })
    }

    setIsOpen = (open: boolean) => {
        const newState: Partial<CategorySelectLogicState> = {
            isOpen: open,
            highlightedIndex: 0,
            showTutorial: false,
        }
        if (!open) {
            newState.filterQuery = ''
            newState.expandedToAll = false
            newState.loadState = 'pristine'
        }
        this.setState(newState)
    }

    toggleTutorial = () => {
        this.setState({ showTutorial: !this.state.showTutorial })
    }

    handleClickOutside = (event: MouseEvent) => {
        if (
            this.containerRef.current &&
            !this.containerRef.current.contains(event.target as Node)
        ) {
            this.closePopout()
        }
    }

    handleClear = async (shiftKey?: boolean) => {
        this.setState({
            selectedCategoryId: undefined,
            textValue: '',
            isOpen: false,
            filterQuery: '',
            expandedToAll: false,
            loadState: 'pristine',
        })

        this.deps.onChange?.(undefined)
        this.deps.clearOnClick?.(shiftKey)
    }

    handleReload = async () => {
        if (!this.deps.transactionId) return

        const cache = this.deps.services.cache
        const current = cache.getData().categoryMatching || {}
        cache.setKeys({
            categoryMatching: {
                ...current,
                [this.deps.transactionId]: 'running',
            },
        })

        try {
            await this.deps.services.bankTransaction.assignCategoryAfterAssociation(
                this.deps.transactionId,
            )
        } catch (e) {
            const after = cache.getData().categoryMatching || {}
            cache.setKeys({
                categoryMatching: {
                    ...after,
                    [this.deps.transactionId]: 'error',
                },
            })
        }
    }

    categorizeAllUncategorizedExpenses = async (): Promise<void> => {
        try {
            const cache = this.deps.services.cache
            const transactions = Object.values(
                cache.getKey('transactions') || {},
            ) as BankTransaction[]
            const uncategorizedExpenses = transactions.filter(
                (tx: BankTransaction) => tx.category === null,
            )
            // TODO: Remove this limit for production
            const limitedExpenses = uncategorizedExpenses.slice(0, 10)
            if (limitedExpenses.length === 0) {
                console.warn('No uncategorized expenses found')
                return
            }
            console.warn(
                `Categorizing ${limitedExpenses.length} uncategorized expenses (limited to first 10 for testing)...`,
            )
            await Promise.all(
                limitedExpenses.map((tx: BankTransaction) =>
                    this.deps.services.bankTransaction.assignCategoryAfterAssociation(
                        tx.id,
                    ),
                ),
            )
            console.warn('Done categorizing all uncategorized expenses')
        } catch (error) {
            console.error(
                'Error categorizing all uncategorized expenses:',
                error,
            )
        }
        this.closePopout()
    }

    removeAllCategories = async (): Promise<void> => {
        try {
            const cache = this.deps.services.cache
            const transactions = Object.values(
                cache.getKey('transactions') || {},
            ) as BankTransaction[]
            if (transactions.length === 0) {
                console.warn('No transactions found')
                return
            }
            console.warn(
                `Removing categories from ${transactions.length} transactions...`,
            )
            await Promise.all(
                transactions.map((tx: BankTransaction) =>
                    this.deps.services.bankTransaction.update(tx.id, {
                        category: undefined,
                    }),
                ),
            )
            console.warn('Done removing all categories from transactions')
        } catch (error) {
            console.error(
                'Error removing categories from all transactions:',
                error,
            )
        }
        this.closePopout()
    }
}
